import { createContext, useContext } from 'react'
import { createStore, useStore, type StoreApi } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import {
  ALWAYS_VISIBLE_COLUMNS, COLUMN_MAX_WIDTH, COLUMN_MIN_WIDTHS, COLUMN_WIDTHS, DEFAULT_COLUMN_VISIBILITY,
  STORAGE_KEY_COLLAPSED, STORAGE_KEY_GANTT, ZOOM_LEVELS, type ColumnId, type ZoomLevel,
} from '../lib/constants'

export type DragState =
  | null
  | { kind: 'resize'; activityId: string; finishDay: number }
  | { kind: 'link'; fromId: string; x: number; y: number; targetId: string | null }

export interface GanttState {
  scheduleId: string
  // persisted globally
  zoom: ZoomLevel
  columnWidths: Record<ColumnId, number>
  columnVisibility: Record<ColumnId, boolean>
  showCriticalPath: boolean
  // persisted per schedule
  collapsedIds: Set<string>
  // session
  showBaseline: boolean
  criticalOnly: boolean
  search: string
  selectedIds: string[]
  anchorId: string | null
  focusedId: string | null
  selectedLinkId: string | null
  editingCell: { activityId: string; column: ColumnId } | null
  panelOpen: boolean
  pendingCount: number
  drag: DragState
  // actions
  setZoom: (zoom: ZoomLevel) => void
  zoomStep: (delta: 1 | -1) => void
  setColumnWidth: (id: ColumnId, px: number) => void
  resetColumnWidth: (id: ColumnId) => void
  toggleColumn: (id: ColumnId) => void
  setShowCriticalPath: (on: boolean) => void
  toggleCollapsed: (id: string) => void
  setCollapsed: (ids: string[], collapsed: boolean) => void
  expandAll: () => void
  setShowBaseline: (on: boolean) => void
  setCriticalOnly: (on: boolean) => void
  setSearch: (q: string) => void
  /** mode 'replace' = click, 'toggle' = ctrl/cmd-click, 'range' = shift-click; `visibleOrder` = visible row ids top→bottom */
  select: (id: string, mode: 'replace' | 'toggle' | 'range', visibleOrder: string[]) => void
  clearSelection: () => void
  setFocused: (id: string | null) => void
  setSelectedLink: (id: string | null) => void
  setEditingCell: (cell: { activityId: string; column: ColumnId } | null) => void
  setPanelOpen: (open: boolean) => void
  incPending: () => void
  decPending: () => void
  setDrag: (drag: DragState) => void
}

const loadCollapsed = (scheduleId: string): Set<string> => {
  try { const raw = localStorage.getItem(STORAGE_KEY_COLLAPSED(scheduleId)); return new Set<string>(raw ? JSON.parse(raw) : []) } catch { return new Set() }
}
const saveCollapsed = (scheduleId: string, ids: Set<string>) => {
  try { localStorage.setItem(STORAGE_KEY_COLLAPSED(scheduleId), JSON.stringify(Array.from(ids))) } catch { /* private mode */ }
}
const clampColumnWidth = (id: ColumnId, px: number): number => Math.min(COLUMN_MAX_WIDTH, Math.max(COLUMN_MIN_WIDTHS[id], Math.round(px)))

/** A column added after the user's visibility map was persisted (e.g. `owner`, Freelo #32277865) is
 * missing from it; missing keys take the default so the new column shows up instead of being
 * silently hidden. Unknown keys are dropped. */
export const sanitizeColumnVisibility = (raw: unknown): Record<ColumnId, boolean> => {
  const src = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  const out = { ...DEFAULT_COLUMN_VISIBILITY }
  ;(Object.keys(DEFAULT_COLUMN_VISIBILITY) as ColumnId[]).forEach((id) => {
    if (typeof src[id] === 'boolean') out[id] = src[id] as boolean
  })
  return out
}

/** Builds a full, valid `columnWidths` map out of whatever a persisted object contains — shared by
 * `migrate` (a stale, pre-Task-2 object that has no `columnWidths` at all) and `merge` (every
 * hydration, including a same-version one, in case a persisted object from an older build or a
 * hand-edited localStorage value is missing a key or carries a bad value). Keeps entries that are a
 * known `ColumnId` with a finite number (clamped to that column's bounds); falls back to the
 * `COLUMN_WIDTHS` default for everything else. The store must never expose an undefined width. */
const sanitizeColumnWidths = (raw: unknown): Record<ColumnId, number> => {
  const source = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  const result = {} as Record<ColumnId, number>
  ;(Object.keys(COLUMN_WIDTHS) as ColumnId[]).forEach((id) => {
    const v = source[id]
    result[id] = typeof v === 'number' && Number.isFinite(v) ? clampColumnWidth(id, v) : COLUMN_WIDTHS[id]
  })
  return result
}

interface PersistedGanttState {
  zoom: ZoomLevel
  columnWidths: Record<ColumnId, number>
  columnVisibility: Record<ColumnId, boolean>
  showCriticalPath: boolean
}

export const createGanttStore = (scheduleId: string): StoreApi<GanttState> =>
  createStore<GanttState>()(
    persist(
      (set, get) => ({
        scheduleId,
        zoom: 'week', columnWidths: { ...COLUMN_WIDTHS }, columnVisibility: { ...DEFAULT_COLUMN_VISIBILITY }, showCriticalPath: true,
        collapsedIds: loadCollapsed(scheduleId),
        showBaseline: true, criticalOnly: false, search: '',
        selectedIds: [], anchorId: null, focusedId: null, selectedLinkId: null, editingCell: null, panelOpen: false, pendingCount: 0, drag: null,

        setZoom: (zoom) => set({ zoom }),
        zoomStep: (delta) => { const i = ZOOM_LEVELS.indexOf(get().zoom); const next = ZOOM_LEVELS[Math.min(ZOOM_LEVELS.length - 1, Math.max(0, i - delta))]; set({ zoom: next }) },   // +1 = zoom in = finer level (lower index)
        setColumnWidth: (id, px) => set({ columnWidths: { ...get().columnWidths, [id]: clampColumnWidth(id, px) } }),
        resetColumnWidth: (id) => set({ columnWidths: { ...get().columnWidths, [id]: COLUMN_WIDTHS[id] } }),
        toggleColumn: (id) => { if (ALWAYS_VISIBLE_COLUMNS.includes(id)) return; set({ columnVisibility: { ...get().columnVisibility, [id]: !get().columnVisibility[id] } }) },
        setShowCriticalPath: (on) => set({ showCriticalPath: on }),
        toggleCollapsed: (id) => { const next = new Set(get().collapsedIds); next.has(id) ? next.delete(id) : next.add(id); saveCollapsed(scheduleId, next); set({ collapsedIds: next }) },
        setCollapsed: (ids, collapsed) => { const next = new Set(get().collapsedIds); ids.forEach((id) => { collapsed ? next.add(id) : next.delete(id) }); saveCollapsed(scheduleId, next); set({ collapsedIds: next }) },
        expandAll: () => { const next = new Set<string>(); saveCollapsed(scheduleId, next); set({ collapsedIds: next }) },
        setShowBaseline: (on) => set({ showBaseline: on }),
        setCriticalOnly: (on) => set({ criticalOnly: on }),
        setSearch: (q) => set({ search: q }),
        // Review fix (Important #6): every branch clears `selectedLinkId` — a row selection and a
        // link selection must never coexist (a range/toggle-select used to leave a previously
        // selected link highlighted, and `Delete` would then remove the link while the user saw
        // rows selected). `setSelectedLink` already clears `selectedIds` the other way.
        select: (id, mode, visibleOrder) => {
          const { selectedIds, anchorId } = get()
          if (mode === 'toggle') { const has = selectedIds.includes(id); set({ selectedIds: has ? selectedIds.filter((x) => x !== id) : [...selectedIds, id], anchorId: id, focusedId: id, selectedLinkId: null }); return }
          if (mode === 'range' && anchorId) {
            const a = visibleOrder.indexOf(anchorId); const b = visibleOrder.indexOf(id)
            if (a >= 0 && b >= 0) { const [lo, hi] = a < b ? [a, b] : [b, a]; set({ selectedIds: visibleOrder.slice(lo, hi + 1), focusedId: id, selectedLinkId: null }); return }
          }
          set({ selectedIds: [id], anchorId: id, focusedId: id, selectedLinkId: null })
        },
        clearSelection: () => set({ selectedIds: [], anchorId: null, selectedLinkId: null }),
        setFocused: (id) => set({ focusedId: id, selectedLinkId: null }),
        setSelectedLink: (id) => set({ selectedLinkId: id, ...(id ? { selectedIds: [] } : {}) }),
        setEditingCell: (cell) => set({ editingCell: cell }),
        setPanelOpen: (open) => set({ panelOpen: open }),
        incPending: () => set({ pendingCount: get().pendingCount + 1 }),
        decPending: () => set({ pendingCount: Math.max(0, get().pendingCount - 1) }),
        setDrag: (drag) => set({ drag }),
      }),
      {
        name: STORAGE_KEY_GANTT,
        storage: createJSONStorage(() => localStorage),
        version: 1,
        partialize: (s): PersistedGanttState => ({ zoom: s.zoom, columnWidths: s.columnWidths, columnVisibility: s.columnVisibility, showCriticalPath: s.showCriticalPath }),
        // Task 2: `gridWidth` is gone from the schema (the grid pane width is derived from
        // `columnWidths` now) — a pre-Task-2 object (`version < 1`) is migrated by dropping the
        // stale `gridWidth` and any other unrecognised key, keeping `zoom`/`columnVisibility`/
        // `showCriticalPath` as-is, and building `columnWidths` from scratch (it never existed
        // before, so `sanitizeColumnWidths` just falls back to the defaults for every column).
        migrate: (persistedState, version): PersistedGanttState => {
          const legacy = persistedState && typeof persistedState === 'object' ? (persistedState as Record<string, unknown>) : {}
          if (version >= 1) return legacy as unknown as PersistedGanttState
          return {
            zoom: (legacy.zoom as ZoomLevel) ?? 'week',
            columnVisibility: sanitizeColumnVisibility(legacy.columnVisibility),
            showCriticalPath: typeof legacy.showCriticalPath === 'boolean' ? legacy.showCriticalPath : true,
            columnWidths: sanitizeColumnWidths(legacy.columnWidths),
          }
        },
        // Runs on every hydration (after `migrate`, when versions differed — otherwise straight off
        // the raw persisted object), so `columnWidths` is re-sanitised here too: a same-version
        // persisted object from an older build within this major version, or one hand-edited/
        // corrupted in localStorage, can still be missing the key or carry a non-finite value. The
        // store must never expose an undefined width.
        merge: (persistedState, currentState) => {
          const p = persistedState && typeof persistedState === 'object' ? (persistedState as Partial<PersistedGanttState>) : {}
          return { ...currentState, ...p, columnWidths: sanitizeColumnWidths(p.columnWidths), columnVisibility: sanitizeColumnVisibility(p.columnVisibility) }
        },
      },
    ),
  )

export const GanttStoreContext = createContext<StoreApi<GanttState> | null>(null)
export const useGanttStoreApi = (): StoreApi<GanttState> => {
  const api = useContext(GanttStoreContext)
  if (!api) throw new Error('useGanttStore outside GanttStoreContext')
  return api
}
export function useGanttStore<T>(selector: (s: GanttState) => T): T {
  return useStore(useGanttStoreApi(), selector)
}
